/**
 * Incident Audit Trail Page - Phase 13
 * 
 * Manager/admin-facing dashboard for offline/recovery incident monitoring and export.
 */

import { useState, useEffect, useMemo } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import {
  AlertTriangle,
  RefreshCw,
  Layout,
  List,
  Clock,
  LayoutGrid
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  IncidentSummaryCards,
  IncidentFilters,
  IncidentTable,
  IncidentTimeline,
  IncidentDetailPanel,
  IncidentExportActions
} from '@/components/incident-audit';
import { useIncidentAudit } from '@/hooks/useIncidentAudit';
import { toast } from 'sonner';

/**
 * Parse URL search params into filter object
 */
function parseUrlFilters(searchParams) {
  const filters = {};
  
  // Status filter
  if (searchParams.get('status')) {
    filters.status = searchParams.get('status');
  }
  
  // Severity filter
  if (searchParams.get('severity')) {
    filters.severity = searchParams.get('severity');
  }
  
  // Domain filter
  if (searchParams.get('domain')) {
    filters.domain = searchParams.get('domain');
  }
  
  // Source type filter
  if (searchParams.get('sourceType')) {
    filters.sourceType = searchParams.get('sourceType');
  }
  
  // Location filter
  if (searchParams.get('locationId')) {
    filters.locationId = searchParams.get('locationId');
  }
  
  // Terminal filter
  if (searchParams.get('terminalId')) {
    filters.terminalId = searchParams.get('terminalId');
  }
  
  // Unresolved only
  if (searchParams.get('unresolvedOnly') === 'true' || searchParams.get('unresolved') === '1') {
    filters.unresolvedOnly = true;
  }
  
  // Search text
  if (searchParams.get('search')) {
    filters.searchText = searchParams.get('search');
  }
  
  return filters;
}

/**
 * Incident Audit Trail Page
 */
export default function IncidentAuditTrailPage() {
  // Get URL search params for inbound filters
  const [searchParams] = useSearchParams();
  
  // Parse URL filters on mount
  const initialFilters = useMemo(() => parseUrlFilters(searchParams), [searchParams]);
  
  // The signed-in back-office user (BackOfficeLayout's outlet context)
  const { manager = null } = useOutletContext() || {};
  const currentUser = manager?.full_name || manager?.name || manager?.email || manager?.username;
  
  // Incident audit hook with initial filters from URL
  const {
    incidents,
    summary,
    filterOptions,
    timeline,
    groupedByDomain,
    groupedByLocation,
    openIncidents,
    criticalIncidents,
    needsAttentionIncidents,
    loading,
    exporting,
    filters,
    updateFilters,
    setFilters,
    clearFilters,
    addNote,
    markReviewed,
    markNeedsFollowup,
    markEscalated,
    dismissIncident,
    exportCsv,
    exportJson,
    copySummary,
    refreshData
  } = useIncidentAudit({ initialFilters });

  // Apply the URL's filters once, on arrival
  const applyUrlFilters = useLatestCallback(() => {
    if (Object.keys(initialFilters).length > 0) {
      updateFilters(initialFilters);
    }
  });
  useEffect(() => {
    applyUrlFilters();
  }, [applyUrlFilters]);

  // Local state
  const [viewMode, setViewMode] = useState('table'); // table | timeline | grouped
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Open detail panel
  const handleViewDetails = (incident) => {
    setSelectedIncident(incident);
    setDetailPanelOpen(true);
  };

  // Close detail panel
  const handleCloseDetail = () => {
    setDetailPanelOpen(false);
    setSelectedIncident(null);
  };

  // Handle filter click from summary cards
  const handleFilterClick = (newFilters) => {
    updateFilters(newFilters);
  };

  // Quick action handlers
  const handleMarkReviewed = async (incident) => {
    if (!currentUser) {
      toast.error('Sign in again to record who reviewed this incident');
      return;
    }
    const result = await markReviewed(incident.incidentId, currentUser);
    if (result.success) {
      toast.success('Marked as reviewed');
    } else {
      toast.error(result.error || 'Failed to mark as reviewed');
    }
  };

  const handleMarkNeedsFollowup = async (incident) => {
    if (!currentUser) {
      toast.error('Sign in again to record who reviewed this incident');
      return;
    }
    const result = await markNeedsFollowup(incident.incidentId, currentUser);
    if (result.success) {
      toast.success('Marked for follow-up');
    } else {
      toast.error(result.error || 'Failed to mark for follow-up');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="incident-audit-trail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6" />
            Incident Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and review offline/recovery incidents across all operations
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <IncidentExportActions
            incidentCount={incidents.length}
            filters={filters}
            onExportCsv={exportCsv}
            onExportJson={exportJson}
            onCopySummary={copySummary}
            disabled={loading}
          />
          
          <Button variant="outline" size="sm" onClick={refreshData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <IncidentSummaryCards 
        summary={summary} 
        onFilterClick={handleFilterClick}
      />

      {/* Alert Banner for Critical Incidents */}
      {criticalIncidents.length > 0 && (
        <Card className="border-red-500/50 bg-red-50 dark:bg-red-900/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="font-medium text-red-700 dark:text-red-400">
                {criticalIncidents.length} critical incident{criticalIncidents.length !== 1 ? 's' : ''} require immediate attention
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => updateFilters({ severity: 'critical' })}
            >
              View Critical
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <IncidentFilters
            filters={filters}
            filterOptions={filterOptions}
            onFilterChange={updateFilters}
            onClearFilters={clearFilters}
            incidentCount={incidents.length}
          />
        </CardContent>
      </Card>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="table" className="flex items-center gap-1">
              <List className="h-4 w-4" />
              Table
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Timeline
            </TabsTrigger>
            <TabsTrigger value="grouped" className="flex items-center gap-1">
              <LayoutGrid className="h-4 w-4" />
              Grouped
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Table View */}
        <TabsContent value="table">
          <Card>
            <CardContent className="p-0">
              <IncidentTable
                incidents={incidents}
                onViewDetails={handleViewDetails}
                onMarkReviewed={handleMarkReviewed}
                onMarkNeedsFollowup={handleMarkNeedsFollowup}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline View */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Event Timeline</CardTitle>
              <CardDescription>Chronological view of incidents</CardDescription>
            </CardHeader>
            <CardContent>
              <IncidentTimeline
                timeline={timeline}
                onViewDetails={handleViewDetails}
                loading={loading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Grouped View */}
        <TabsContent value="grouped">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Domain */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">By Domain</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {Object.entries(groupedByDomain).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No incidents</p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(groupedByDomain).map(([domain, domainIncidents]) => (
                        <div key={domain}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium capitalize">{domain}</h4>
                            <span className="text-sm text-muted-foreground">
                              {domainIncidents.length} incident{domainIncidents.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {domainIncidents.slice(0, 5).map(incident => (
                              <div 
                                key={incident.incidentId}
                                className="p-2 bg-muted/50 rounded-lg text-sm cursor-pointer hover:bg-muted transition-colors"
                                onClick={() => handleViewDetails(incident)}
                              >
                                <p className="font-medium truncate">{incident.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(incident.occurredAt).toLocaleString()}
                                </p>
                              </div>
                            ))}
                            {domainIncidents.length > 5 && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="w-full"
                                onClick={() => updateFilters({ domain })}
                              >
                                View all {domainIncidents.length} in {domain}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* By Location */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">By Location</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {Object.entries(groupedByLocation).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No incidents</p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(groupedByLocation).map(([location, locationIncidents]) => (
                        <div key={location}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{location === 'no-location' ? 'No Location' : location}</h4>
                            <span className="text-sm text-muted-foreground">
                              {locationIncidents.length} incident{locationIncidents.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {locationIncidents.slice(0, 5).map(incident => (
                              <div 
                                key={incident.incidentId}
                                className="p-2 bg-muted/50 rounded-lg text-sm cursor-pointer hover:bg-muted transition-colors"
                                onClick={() => handleViewDetails(incident)}
                              >
                                <p className="font-medium truncate">{incident.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(incident.occurredAt).toLocaleString()}
                                </p>
                              </div>
                            ))}
                            {locationIncidents.length > 5 && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="w-full"
                                onClick={() => updateFilters({ locationId: location })}
                              >
                                View all {locationIncidents.length} at {location}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Panel */}
      <IncidentDetailPanel
        incident={selectedIncident}
        open={detailPanelOpen}
        onClose={handleCloseDetail}
        onAddNote={addNote}
        onMarkReviewed={markReviewed}
        onMarkNeedsFollowup={markNeedsFollowup}
        onMarkEscalated={markEscalated}
        onDismiss={dismissIncident}
        currentUser={currentUser}
      />
    </div>
  );
}
